/**
 * Centralized Error Handler for API requests
 * Feature: 007-cloudflare-migration
 *
 * Provides standardized error handling with Sentry integration
 * and localized user feedback.
 */

import * as Sentry from '@sentry/react'
import type { ApiErrorResponse } from './types'

// ============================================================================
// Error Types
// ============================================================================

/**
 * Machine-readable error codes from API
 */
export const ApiErrorCode = {
  // Authentication errors
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  INVALID_TOKEN: 'INVALID_TOKEN',

  // Validation errors
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  INVALID_REQUEST: 'INVALID_REQUEST',

  // Resource errors
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',

  // Rate limiting
  RATE_LIMITED: 'RATE_LIMITED',

  // Server errors
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',

  // Network errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT',

  // Unknown
  UNKNOWN: 'UNKNOWN',
} as const

export type ApiErrorCode = (typeof ApiErrorCode)[keyof typeof ApiErrorCode]

/**
 * Structured API error for application use
 */
export class ApiError extends Error {
  public readonly code: ApiErrorCode
  public readonly status: number
  public readonly originalError?: unknown

  constructor(
    message: string,
    code: ApiErrorCode,
    status: number = 500,
    originalError?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.originalError = originalError

    // Maintains proper prototype chain for instanceof checks
    Object.setPrototypeOf(this, ApiError.prototype)
  }

  /**
   * Get i18n translation key for this error
   */
  get translationKey(): string {
    return `api.errors.${this.code.toLowerCase()}`
  }

  /**
   * Check if this is an authentication error
   */
  get isAuthError(): boolean {
    return (
      this.code === ApiErrorCode.UNAUTHORIZED ||
      this.code === ApiErrorCode.FORBIDDEN ||
      this.code === ApiErrorCode.SESSION_EXPIRED ||
      this.code === ApiErrorCode.INVALID_TOKEN
    )
  }

  /**
   * Check if this is a network/connectivity error
   */
  get isNetworkError(): boolean {
    return (
      this.code === ApiErrorCode.NETWORK_ERROR ||
      this.code === ApiErrorCode.TIMEOUT ||
      this.code === ApiErrorCode.SERVICE_UNAVAILABLE
    )
  }

  /**
   * Check if this error is retryable
   */
  get isRetryable(): boolean {
    return (
      this.isNetworkError ||
      this.code === ApiErrorCode.RATE_LIMITED ||
      this.status >= 500
    )
  }
}

// ============================================================================
// Error Parsing
// ============================================================================

/**
 * Map HTTP status codes to error codes
 */
function mapStatusToCode(status: number): ApiErrorCode {
  switch (status) {
    case 400:
      return ApiErrorCode.VALIDATION_ERROR
    case 401:
      return ApiErrorCode.UNAUTHORIZED
    case 403:
      return ApiErrorCode.FORBIDDEN
    case 404:
      return ApiErrorCode.NOT_FOUND
    case 409:
      return ApiErrorCode.CONFLICT
    case 429:
      return ApiErrorCode.RATE_LIMITED
    case 503:
      return ApiErrorCode.SERVICE_UNAVAILABLE
    default:
      return status >= 500 ? ApiErrorCode.INTERNAL_ERROR : ApiErrorCode.UNKNOWN
  }
}

/**
 * Parse API error response into structured ApiError
 */
export function parseApiError(
  response: Response,
  body?: ApiErrorResponse
): ApiError {
  // Use code from body if available, otherwise map from status
  const code = body?.code
    ? (body.code as ApiErrorCode) || mapStatusToCode(response.status)
    : mapStatusToCode(response.status)

  const message = body?.message || response.statusText || 'An error occurred'

  return new ApiError(message, code, response.status)
}

/**
 * Handle network/fetch errors (no response received)
 */
export function handleNetworkError(error: unknown): ApiError {
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return new ApiError(
      'Unable to connect to server',
      ApiErrorCode.NETWORK_ERROR,
      0,
      error
    )
  }

  if (error instanceof DOMException && error.name === 'AbortError') {
    return new ApiError('Request timed out', ApiErrorCode.TIMEOUT, 0, error)
  }

  return new ApiError(
    'An unexpected error occurred',
    ApiErrorCode.UNKNOWN,
    0,
    error
  )
}

// ============================================================================
// Sentry Integration
// ============================================================================

/**
 * Report error to Sentry with appropriate context
 */
export function reportErrorToSentry(
  error: ApiError,
  context?: {
    endpoint?: string
    method?: string
    params?: Record<string, unknown>
  }
): void {
  // Don't report auth errors (expected user flow)
  if (error.code === ApiErrorCode.UNAUTHORIZED) {
    Sentry.addBreadcrumb({
      category: 'api',
      message: 'Unauthorized request',
      level: 'info',
      data: context,
    })
    return
  }

  // Don't report 404s (usually expected)
  if (error.code === ApiErrorCode.NOT_FOUND) {
    Sentry.addBreadcrumb({
      category: 'api',
      message: 'Resource not found',
      level: 'info',
      data: context,
    })
    return
  }

  // Report all other errors
  Sentry.captureException(error.originalError || error, {
    tags: {
      feature: 'cloudflare-migration',
      error_code: error.code,
      http_status: error.status.toString(),
    },
    contexts: {
      api_request: {
        endpoint: context?.endpoint,
        method: context?.method,
        ...context?.params,
      },
    },
  })
}

// ============================================================================
// Error Utilities
// ============================================================================

/**
 * Check if an error is an ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

/**
 * Create a user-friendly error message
 * Falls back to generic message if translation not available
 */
export function getErrorMessage(error: unknown, t: (key: string) => string): string {
  if (isApiError(error)) {
    // Try to get translated message
    const translated = t(error.translationKey)
    // If translation key is returned unchanged, use original message
    return translated === error.translationKey ? error.message : translated
  }

  if (error instanceof Error) {
    return error.message
  }

  return t('api.errors.unknown')
}
